<template>
  <v-card class="pa-8 d-flex flex-column" min-height="100%">
    <v-card-title class="text-h4 text-primary font-weight-regular">
      {{ $t('LoginFormTitle') }}
    </v-card-title>
    <v-form v-if="config.public.AUTH_LOCAL_LOGIN === 'y'" v-model="valid" @submit.prevent="login">
      <!--    <v-form v-if="false" v-model="valid" @submit.prevent="login">-->
      <v-card-text>
        <!-- Username/Email -->
        <v-text-field
          v-model="username"
          :label="$t('RegisterFormLabelName')"
          persistent-placeholder
          variant="outlined"
          :rules="rules"
          autofocus
        />

        <!-- Password -->
        <v-text-field
          v-model="password"
          :label="$t('RegisterFormLabelPassword')"
          persistent-placeholder
          type="password"
          variant="outlined"
          :rules="rules"
        />
      </v-card-text>
      <v-card-actions>
        <!-- Register -->
        <v-btn color="primary" :to="{ name: Routes.auth_register }" type="button" class="flex-1-1-0">
          {{ $t('RegisterButton') }}
        </v-btn>

        <!-- Login -->
        <v-btn
          color="primary"
          :disabled="!valid"
          :loading="loading"
          variant="flat"
          type="submit"
          class="flex-1-1-0 text-surface-bright"
          @mouseenter="clearAutologin"
        >
          {{ $t('LoginFormButtonSubmit') }}
        </v-btn>
      </v-card-actions>
      <v-divider class="my-4" />
    </v-form>

    <!-- SSO Login -->
    <v-card-text class="d-flex align-center justify-center">
      <!-- SSO Actions -->
      <v-btn-group>
        <v-btn
          :href="$config.public.API_SSO_URL"
          :disabled="!$config.public.API_SSO_URL"
          variant="elevated"
          size="large"
          append-icon="mdi-open-in-new"
          class="text-white bg-primary text-surface-bright"
        >
          {{ $t('LoginSOO') }}
        </v-btn>

        <!-- SSO Debug -->
        <v-menu :close-on-content-click="false" :disabled="!$config.public.API_SSO_URL">
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="outlined" size="small" icon="mdi-dots-vertical" />
          </template>

          <v-defaults-provider :defaults="{ VBtn: { density: 'compact' } }">
            <v-list density="compact" min-width="450">
              <v-list-item>
                <v-list-item-subtitle>
                  {{ $t('LoginSOODebugExpand') }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item
                :title="$t('LoginSOODebugRequestMetadataIdp')"
                :subtitle="$t('LoginSOODebugRequestMetadataIdpSubtitle')"
              >
                <template #append>
                  <div class="d-flex flex-row">
                    <v-btn href="/api/auth/saml/metadata_idp" target="_blank" icon="mdi-open-in-new" />
                    <v-btn icon="mdi-download" @click="downloadMetadataIdp" />
                  </div>
                </template>
              </v-list-item>
              <v-list-item
                :title="$t('LoginSOODebugRequestMetadataSp')"
                :subtitle="$t('LoginSOODebugRequestMetadataSpSubtitle')"
              >
                <template #append>
                  <div class="d-flex flex-row">
                    <v-btn href="/api/auth/saml/metadata" target="_blank" icon="mdi-open-in-new" />
                    <v-btn icon="mdi-download" @click="downloadMetadataSp" />
                  </div>
                </template>
              </v-list-item>
              <v-list-item :title="$t('LoginSOODebugRequestInfo')" :subtitle="$t('LoginSOODebugRequestInfoSubtitle')">
                <template #append>
                  <v-btn :href="$config.public.API_SSO_URL + '_debug_request'" target="_blank" icon="mdi-open-in-new" />
                </template>
              </v-list-item>
              <v-list-item :title="$t('LoginSOODebugResponseInfo')" :subtitle="$t('LoginSOODebugResponseInfoSubtitle')">
                <template #append>
                  <v-btn
                    :href="$config.public.API_SSO_URL + '_debug_response'"
                    target="_blank"
                    icon="mdi-open-in-new"
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-defaults-provider>
        </v-menu>
      </v-btn-group>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { definePageMeta, useRuntimeConfig } from '#imports'
import { useHead } from 'nuxt/app'
import { useI18n } from 'vue-i18n'
import useUser from '~/composables/useUser'
import { AuthRedirects, GuestMode } from '~/lib/auth'
import { Routes } from '~/_routes'
import { FormRules } from '~/helper/form-rules'
import { Notify } from '~/lib/notify'
import { injectBackendClient } from '~/api/backendClients'
import { axiosDownloadFile } from '~/helper/download'

export default defineComponent({
  name: 'LoginPage',
  setup() {
    const { t } = useI18n()
    const backend = injectBackendClient()
    definePageMeta({ auth: GuestMode })
    useHead({
      title: t('PageLogin'),
    })
    const { auth, notifyTokenExpiration, checkUsersTimeout } = useUser()

    async function downloadMetadataIdp() {
      const response = await backend.authApi.samlMetadataIdp({
        responseType: 'blob',
      })
      axiosDownloadFile(response, 'hans-saml-idp-metadata')
    }

    async function downloadMetadataSp() {
      const response = await backend.authApi.samlMetadata({
        responseType: 'blob',
      })
      axiosDownloadFile(response, 'hans-saml-sp-metadata')
    }

    return {
      auth,
      checkUsersTimeout,
      notifyTokenExpiration,
      Routes,
      config: useRuntimeConfig(),
      downloadMetadataIdp: downloadMetadataIdp,
      downloadMetadataSp: downloadMetadataSp,
    }
  },
  data() {
    return {
      loading: false,
      valid: true,
      username: '',
      password: '',
      autoLogin: null as NodeJS.Timeout | null,
    }
  },
  computed: {
    rules() {
      return [FormRules.required(this)]
    },
  },
  created() {
    this.username = this.config.public.ADMIN_USERNAME ?? ''
    this.password = this.config.public.ADMIN_PASSWORD ?? ''
  },
  mounted() {
    // autologin on dev
    const autologin = false
    if (autologin && import.meta.dev) {
      Notify.info(this.$t('DevLoginAutologinTriggered'))
      this.autoLogin = setTimeout(() => {
        if (this.username && this.password) {
          this.login()
        }
      }, 1500)
    }
  },
  methods: {
    clearAutologin() {
      if (this.autoLogin) {
        clearTimeout(this.autoLogin)
        Notify.warning(this.$t('DevLoginAutologinAborted'))
        this.autoLogin = null
      }
    },
    async login() {
      this.loading = true
      try {
        const formdata = new FormData()
        formdata.set('username', this.username)
        formdata.set('password', this.password)

        // Provide a callbackUrl when no redirect, otherwise router will throw a "no external allowed" error
        const options = {
          callbackUrl: this.$route.query.redirect ? undefined : AuthRedirects.home,
        }
        await this.auth.signIn(formdata, options)

        Notify.success(this.$t('LoginSuccess'))
        // use nextTick() otherwise vue-sonner layer-bug
        this.$nextTick(this.notifyTokenExpiration)
        this.$nextTick(this.checkUsersTimeout)
      } catch (e) {
        this.$handleFetchError(e)
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
